import {chunk} from 'lodash-es'
import {Fragment, memo, useMemo, useRef} from 'react'

import {useTableCopilotTasks} from '../../features/copilot-tasks/use-table-copilot-tasks'
import {useSubIssuesLoading} from '../../features/sub-issues/use-sub-issues-loading'
import {useTableHierarchy} from '../../features/sub-issues/use-table-hierarchy'
import type {Row} from '../../hooks/tanstack-table/helpers/types'
import {useTableInstance} from '../../hooks/tanstack-table/providers/table-provider'
import type {MemexItemModel} from '../../models/memex-item-model'
import useIsVisible from '../board/hooks/use-is-visible'
import {AgentTaskSubRow} from './agent-task-sub-row'
import {CELL_HEIGHT} from './constants'
import {useTableNavigation} from './navigation'
import {useIsDraggingRows} from './row-reordering/hooks/use-is-dragging-rows'
import type {TableDataType} from './table-data-type'
import {SubIssuesPlaceholderRow} from './table-pagination'
import {TableRow, type TableRowProps} from './table-row'

/** The number of rows that will be rendered in a batch as they are scrolled into view. */
export const VIRTUALIZATION_CHUNK_SIZE = 5

/**
 * This represents a set of rows that act as a single unit of virtualization in
 * the vertical scroll direction.
 *
 * In other words, as the user scrolls vertically, we will render a new
 * TableRowChunk as it becomes visible (as opposed to rendering each TableRow
 * individually).
 */
export const TableRowChunk = memo(function TableRowChunk({
  rows,
  itemData,
}: {
  rows: Array<Row<TableDataType>>
  itemData: TableRowProps['data']
}) {
  const ref = useRef<HTMLDivElement | null>(null)
  const {isVisible, size} = useIsVisible({ref, defaultHeight: CELL_HEIGHT * rows.length})
  const {
    state: {focus},
  } = useTableNavigation()
  const {hierarchyEnabled} = useTableHierarchy()
  const {copilotTasksEnabled} = useTableCopilotTasks()

  const isFocused = focus && focus.type === 'coordinate' ? rows.some(row => row.id === focus.details.y) : false

  // If we don't render the rows when dragging, the `useReorderableRow` hook will get unmounted and dnd-kit will not
  // provide any drop data
  const isDragging = useIsDraggingRows(rows)

  const shouldRender = isVisible || isFocused || isDragging

  // When this chunk of rows is visible, we set the height to 'unset' so that we inherit the true
  // height of the chunk after all of its contents have been painted. Otherwise, we render a fixed
  // height as a placeholder.
  const height = isVisible ? 'unset' : `${size}px`

  const style = useMemo(() => ({height}), [height])

  return (
    <div style={style} ref={ref}>
      {shouldRender
        ? rows.map(row => {
            // Nested rows marked as defaultVisible render without requiring expand
            const defaultVisibleSubRows = copilotTasksEnabled
              ? row.subRows.filter(r => r.subIssueData?.defaultVisible)
              : []
            return (
              <Fragment key={row.id}>
                <TableRow key={row.id} row={row} index={row.index} data={itemData} />
                {copilotTasksEnabled && (
                  <AgentTaskSubRow model={row.original} depth={row.subIssueData?.subIssueDepth ?? 0} />
                )}
                {defaultVisibleSubRows.length > 0 && (
                  <DefaultVisibleNestedChunk rows={defaultVisibleSubRows} itemData={itemData} />
                )}
                {hierarchyEnabled && row.getIsExpanded() && (
                  <NestedSubIssuesChunk parentRow={row} itemData={itemData} />
                )}
              </Fragment>
            )
          })
        : null}
    </div>
  )
})

/**
 * Renders the expandable sub-issues portion of nested content.
 * Filters out defaultVisible rows since those are rendered separately.
 */
const NestedSubIssuesChunk = ({
  parentRow,
  itemData,
}: {
  parentRow: Row<MemexItemModel>
  itemData: TableRowProps['data']
}) => {
  const {isLoadingSubIssues} = useSubIssuesLoading()
  const {getVisibleLeafColumns} = useTableInstance()
  const visibleColumns = getVisibleLeafColumns()

  if (isLoadingSubIssues(parentRow)) {
    return <SubIssuesPlaceholderRow columns={visibleColumns} depth={parentRow.subIssueData?.subIssueDepth} />
  }

  // Only render rows that are NOT defaultVisible (those require expand to be seen)
  const expandableRows = parentRow.subRows.filter(r => !r.subIssueData?.defaultVisible)

  return (
    <Fragment>
      {chunk(expandableRows, VIRTUALIZATION_CHUNK_SIZE).map((rowChunk, index) => (
        <TableRowChunk
          // eslint-disable-next-line @eslint-react/no-array-index-key
          key={index}
          rows={rowChunk}
          itemData={itemData}
        />
      ))}
    </Fragment>
  )
}

/**
 * Renders nested rows that are always visible (e.g. agent tasks).
 * These don't require the parent row to be expanded.
 */
const DefaultVisibleNestedChunk = ({
  rows,
  itemData,
}: {
  rows: Array<Row<MemexItemModel>>
  itemData: TableRowProps['data']
}) => {
  return (
    <Fragment>
      {chunk(rows, VIRTUALIZATION_CHUNK_SIZE).map((rowChunk, index) => (
        <TableRowChunk
          // eslint-disable-next-line @eslint-react/no-array-index-key
          key={index}
          rows={rowChunk}
          itemData={itemData}
        />
      ))}
    </Fragment>
  )
}
